import { Payment, PaymentMethodType, StripeCard } from '../../../clients/quipli-service/payment/payment';
import { PaymentMethodSelectorV2 } from '../../payment/payment-method-selector-v2';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { ReactNode, useEffect, useState } from 'react';
import { Order } from '../../../clients/quipli-service/order/order';
import { PaymentTotalSelector } from './payment-total-selector';
import { PaymentContextProvider } from '../../../context/payment-context';
import { convertToMoney, dineroSum } from '../../../util/util';
import { Currency } from 'dinero.js';
import { Button } from '../../../components/form/button';
import { checkInvoicePaymentStatus } from '../../payment/payment-utils';

export const StatusTransitionPayment = ({
  order,
  paymentMethod,
  setPaymentMethod,
  amount,
  setAmount,
  total,
  paymentTotal,
  defaultPercentage,
  card,
  payments,
  setPayments,
  facilitate,
  onComplete,
}: {
  order: Order;
  paymentMethod?: PaymentMethodRequestV2;
  setPaymentMethod: (pm?: PaymentMethodRequestV2) => void;
  amount?: string;
  setAmount: (a: string) => void;
  total: number;
  paymentTotal: number;
  defaultPercentage?: number;
  card?: StripeCard;
  payments?: Payment[];
  setPayments?: (p: Payment[]) => void;
  facilitate?: boolean;
  onComplete: (payments?: Payment[]) => void;
}) => {
  const [percent, setPercent] = useState<number>();
  const [confirmPending, setConfirmPending] = useState<boolean>(false);
  const [additionalText, setAdditionalText] = useState<ReactNode | undefined>();
  const balanceDue = total - paymentTotal;

  useEffect(() => {
    const { suspicious, pending } = order.invoices.map(checkInvoicePaymentStatus).reduce(
      (acc, res) => {
        return { suspicious: [...acc.suspicious, ...res.suspicious], pending: [...acc.pending, ...res.pending] };
      },
      { suspicious: [], pending: [] },
    );

    const pendingMessage = pending.length ? (
      <>
        There is a pending transaction balance of{' '}
        {dineroSum(
          pending.flatMap((p) => p.invoicePayments.map((p) => convertToMoney(p.amount, 'usd' as Currency))),
          'usd' as Currency,
        ).toFormat()}{' '}
        on this order.
        <br />
        Continuing may result in duplicate charges.
        <br />
      </>
    ) : (
      <></>
    );
    const suspiciousMessage = suspicious.length ? (
      <>
        There are suspicious transactions on this order.
        <br />
        Continuing may result in a cardholder dispute or theft.
      </>
    ) : (
      <></>
    );

    setAdditionalText(
      <div>
        {pendingMessage} {suspiciousMessage}
      </div>,
    );

    setConfirmPending(!pending.length && !suspicious.length);
  }, []);

  return !confirmPending ? (
    <div className="flex flex-column gap-2 mt-2">
      {additionalText}
      <Button className={'button button-red'} onClick={() => setConfirmPending(true)}>
        Confirm
      </Button>
    </div>
  ) : (
    <div className="flex flex-column gap-2">
      <PaymentTotalSelector
        paymentMethodType={paymentMethod?.type}
        total={total}
        paymentTotal={paymentTotal}
        defaultPercentage={defaultPercentage}
        setPercent={setPercent}
        percent={percent}
        amount={amount}
        setAmount={setAmount}
      />

      {amount && (
        <PaymentContextProvider>
          <PaymentMethodSelectorV2
            user={order.user}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            locationId={order.locationId}
            card={card}
            payments={payments}
            setPayments={setPayments}
            facilitate={facilitate}
            onComplete={(payments) => {
              onComplete(payments);
            }}
            setAmount={(a) => setAmount(a.toFixed(2))}
            balance={balanceDue}
          />
        </PaymentContextProvider>
      )}
    </div>
  );
};

export function orderToPaymentMethodV2(order: Order): PaymentMethodRequestV2 | undefined {
  let pm: PaymentMethodRequestV2 | undefined = undefined;
  switch (order.paymentMethod.type) {
    case PaymentMethodType.Stripe:
      pm = {
        type: PaymentMethodType.Stripe,
        cardId: order.paymentMethod.card?.id,
        method: order.paymentMethod.method,
        bankAccountId: order.paymentMethod.bankAccount?.id,
      };
      break;
    case PaymentMethodType.Credit:
    case PaymentMethodType.OnAccount:
    case PaymentMethodType.None:
      break;
    default:
      pm = order.paymentMethod;
  }
  return pm;
}
